/**
 * 2026-08-24 — pure helpers for the /mcp OAuth-clients panel.
 *
 * The scope universe the panel offers is DERIVED from the catalog on
 * every render — the union of `required_scopes.agent` across
 * agent-callable actions — never a hand-written list (the /config
 * chat-tools section once kept its own copy of an exclusion list and
 * rendered 15 dead checkboxes; see `chat_hard_excluded` in types.ts).
 * A scope that unlocks nothing for an OAuth client is not offered.
 *
 * Granted-but-unknown scopes (a legacy grant whose action was renamed
 * or retired) are NOT dropped: `groupScopesForClient` folds them in so
 * the operator can SEE them and a save round-trips them deliberately
 * rather than silently erasing them.
 */

/** One catalog entry, reduced to what scope derivation needs. */
export interface ScopeSourceAction {
  readonly name: string;
  readonly allowed_actors: ReadonlyArray<string>;
  readonly required_scopes: Record<string, ReadonlyArray<string>>;
}

/**
 * scope → sorted names of the agent-callable actions that require it.
 * The map's keys are the offerable universe; the values power the
 * "unlocks N tools" hint per checkbox.
 */
export function deriveAgentScopeUniverse(
  actions: ReadonlyArray<ScopeSourceAction>,
): Map<string, ReadonlyArray<string>> {
  const byScope = new Map<string, string[]>();
  for (const a of actions) {
    if (!a.allowed_actors.includes("agent")) continue;
    const agentScopes = a.required_scopes["agent"] ?? [];
    for (const scope of agentScopes) {
      const list = byScope.get(scope) ?? [];
      list.push(a.name);
      byScope.set(scope, list);
    }
  }
  const sorted = new Map<string, ReadonlyArray<string>>();
  for (const key of [...byScope.keys()].sort()) {
    sorted.set(key, [...byScope.get(key)!].sort());
  }
  return sorted;
}

export interface ScopeFamilyGroup {
  /** The prefix before `:` — `members`, `bookings`, `knowledge_base`… */
  readonly family: string;
  readonly scopes: ReadonlyArray<{
    readonly scope: string;
    /** Agent-callable actions this scope unlocks; empty for legacy grants. */
    readonly unlocks: ReadonlyArray<string>;
    /** True when the scope is NOT in the derived universe (legacy grant). */
    readonly unknown: boolean;
  }>;
}

/**
 * Group the offerable universe PLUS this client's granted-but-unknown
 * scopes into families for rendering. Families and scopes sort
 * alphabetically; reads (`:read`) sort before writes within a family
 * only by that same alphabetical rule — no hidden ordering.
 */
export function groupScopesForClient(
  universe: Map<string, ReadonlyArray<string>>,
  granted: ReadonlyArray<string>,
): ReadonlyArray<ScopeFamilyGroup> {
  const all = new Map<
    string,
    { unlocks: ReadonlyArray<string>; unknown: boolean }
  >();
  for (const [scope, unlocks] of universe) {
    all.set(scope, { unlocks, unknown: false });
  }
  for (const scope of granted) {
    if (!all.has(scope)) all.set(scope, { unlocks: [], unknown: true });
  }
  const families = new Map<string, ScopeFamilyGroup["scopes"][number][]>();
  for (const [scope, meta] of all) {
    const family = scope.split(":")[0] ?? scope;
    const list = families.get(family) ?? [];
    list.push({ scope, unlocks: meta.unlocks, unknown: meta.unknown });
    families.set(family, list);
  }
  return [...families.keys()].sort().map((family) => ({
    family,
    scopes: [...families.get(family)!].sort((a, b) =>
      a.scope.localeCompare(b.scope),
    ),
  }));
}

/** Set-equality on scope lists — order and duplicates don't count. */
export function scopeSetsEqual(
  a: ReadonlyArray<string>,
  b: ReadonlyArray<string>,
): boolean {
  const sa = new Set(a);
  const sb = new Set(b);
  if (sa.size !== sb.size) return false;
  for (const s of sa) if (!sb.has(s)) return false;
  return true;
}
